import React, {useEffect, useState} from 'react';
import * as Yup from 'yup';
import clsx from 'clsx';
import {useFormik} from 'formik';
import {useIntl} from "react-intl";
import {isNotEmpty} from "../../../../_metronic/helpers";
import {useNavigate, useParams} from "react-router-dom";
import {NotificationMessage} from "../../../shared/helper/Notification";
import {activateUser, getUserForActivation} from "../../../shared/services/user-service";
import {SimpleLoading} from "../../apps/shared/list/components/loading/SimpleLoading";

const initialValues = {
    password: '',
    password_repeat: '',
}

export function ActivateAccount() {
    const intl = useIntl();
    const navigate = useNavigate();
    const activaitonCode = useParams()['*'] || 'error';
    const [loading, setLoading] = useState(false);
    const [user, setUser] = useState({} as any);

    useEffect(() => {
            getUserForActivation(activaitonCode, '')
                .then((response: any) => {
                    if (response?.errors) {
                        backToLogin();
                    } else {
                        setUser(response);
                    }
                })
        },
        // eslint-disable-next-line
        []);

    const activationAccountSchema = Yup.object().shape({
        password: Yup.string()
            .matches(
                /^.*(?=.{8,})((?=.*[!@#$%^&*()\-_=+{};:,<.>]){1})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/,
                intl.formatMessage({id: 'ACCOUNT.USER_DETAILS.INVALID_PASSWORD'})
            )
            .required(intl.formatMessage({id: 'REQUIRED_FIELD'}))
            .nullable(),
        password_repeat: Yup.string()
            .nullable()
            .required(intl.formatMessage({id: 'REQUIRED_FIELD'}))
            .when('password', {
                is: (password: string) => isNotEmpty(password),
                then: (password_repeat) => password_repeat.required(intl.formatMessage({id: 'REQUIRED_FIELD'}))
                    .oneOf([Yup.ref('password'), null], intl.formatMessage({id: 'ACCOUNT.USER_DETAILS.EQUAL_PASSWORD'}))
            })
    });

    function backToLogin() {
        navigate(`/login`);
    }

    const formik = useFormik({
        initialValues,
        validationSchema: activationAccountSchema,
        onSubmit: (values) => {
            setLoading(true);
            try {
                activateUser(user?.id, values).then(() => {
                    NotificationMessage(intl.formatMessage({id: 'MESSAGE_NOTIFICATION.SUCCESSFUL_ACTIVATION'}), intl.formatMessage({id: 'MESSAGE_NOTIFICATION.LOGIN_PLEASE'}), 'success');
                    backToLogin();
                })
            } catch (e) {
                NotificationMessage(intl.formatMessage({id: 'MESSAGE_NOTIFICATION.NOT_SUCCESSFUL_ACTIVATION'}), intl.formatMessage({id: 'MESSAGE_NOTIFICATION.CONTACT_US'}), 'danger');
                backToLogin();
            }
        },
    })

    return (
        <>
            {!user.id ? <div className='py-2'>
                    <SimpleLoading/>
                </div> :
                <>
                    <div className='text-center mb-5'>
                        <h1 className='text-dark mb-3'>{intl.formatMessage({id: 'ACTIVATION_USER'})}</h1>
                        <div className='text-gray-400 fw-bold fs-4'>
                            {intl.formatMessage({id: 'ACTIVATION_USER.INFO'})}
                        </div>
                    </div>
                    <form
                        className='form w-100'
                        onSubmit={formik.handleSubmit}
                        noValidate
                    >
                        <div className='fv-row mb-10'>
                            <label
                                className='form-label fs-6 fw-bolder text-dark'>{intl.formatMessage({id: 'ACTIVATION_USER.PASSWORD'})}</label>
                            <input
                                placeholder={intl.formatMessage({id: 'ACTIVATION_USER.PASSWORD'})}
                                {...formik.getFieldProps('password')}
                                className={clsx(
                                    'form-control form-control-lg form-control-solid',
                                    {
                                        'is-invalid border border-danger': formik.touched.password && formik.errors.password,
                                        'is-valid border border-success': formik.touched.password && !formik.errors.password,
                                    },
                                )}
                                type='password'
                                name='password'
                                autoComplete='off'
                            />
                            {formik.touched.password && formik.errors.password && (
                                <div className='fv-plugins-message-container'>
                                    <div className='fv-help-block'>
                                        <span role='alert'>{formik.errors.password}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className='fv-row mb-10'>
                            <div className='d-flex justify-content-between mt-n5'>
                                <label
                                    className='form-label fs-6 fw-bolder text-dark'>{intl.formatMessage({id: 'ACTIVATION_USER.PASSWORD_REPEAT'})}</label>
                            </div>
                            <input
                                type='password'
                                placeholder={intl.formatMessage({id: 'ACTIVATION_USER.PASSWORD_REPEAT'})}
                                autoComplete='off'
                                {...formik.getFieldProps('password_repeat')}
                                className={clsx(
                                    'form-control form-control-lg form-control-solid',
                                    {
                                        'is-invalid border border-danger': formik.touched.password_repeat && formik.errors.password_repeat,
                                        'is-valid border border-success': formik.touched.password_repeat && !formik.errors.password_repeat,
                                    },
                                )}
                            />
                            {formik.touched.password_repeat && formik.errors.password_repeat && (
                                <div className='fv-plugins-message-container'>
                                    <div className='fv-help-block'>
                                        <span role='alert'>{formik.errors.password_repeat}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
                            <button
                                type='submit'
                                className='btn btn-lg btn-primary fw-bolder me-4'
                                disabled={loading}
                            >
                                {!loading && intl.formatMessage({id: 'ACTIVATION_USER.CONFIRM'})}
                                {loading && (
                                    <span className='indicator-progress' style={{display: 'block'}}>
                                    {intl.formatMessage({id: 'ACTIVATION_USER.ACTIVATING'})}...{' '}
                                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                </span>
                                )}
                            </button>
                            <button
                                type='button'
                                onClick={() => backToLogin()}
                                className='btn btn-lg btn-light-primary fw-bolder'
                                disabled={loading}
                            >
                                {intl.formatMessage({id: 'ACTIVATION_USER.CANCEL'})}
                            </button>
                        </div>
                    </form>
                </>
            }
        </>
    )
}
